/**
 * HotLeadQueueManager Component
 * 
 * Admin interface for managing the Hot Lead Queue.
 * Features:
 * - CSV upload for importing refinance opportunities
 * - Queue statistics and batch management
 * - Manual recycle controls
 * - View and manage queue settings
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Icon,
  Progress,
  Alert,
  AlertIcon,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Divider,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Spinner,
  Center,
  Link,
} from '@chakra-ui/react';
import { 
  FiUpload, 
  FiRefreshCw, 
  FiTrash2, 
  FiDownload,
  FiCheckCircle,
  FiClock,
  FiUsers,
  FiZap,
  FiExternalLink,
} from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { supabase } from '../../../utils/supabase';
import Papa from 'papaparse';
import { formatDistanceToNow } from 'date-fns';

export function HotLeadQueueManager({ toast, currentUser, crmUsers }) {
  // State
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [recycling, setRecycling] = useState(false);
  const [queueStats, setQueueStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [lastImportResult, setLastImportResult] = useState(null);
  
  // Form state
  const [batchName, setBatchName] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [parsedRecords, setParsedRecords] = useState(null);
  
  const fileInputRef = useRef(null);
  const { isOpen: isResultOpen, onOpen: onResultOpen, onClose: onResultClose } = useDisclosure();

  // Colors
  const cardBg = useColorModeValue('gray.50', 'gray.700');

  /**
   * Fetch queue statistics
   */
  const fetchQueueStats = useCallback(async () => {
    if (!supabase) return;
    
    setLoading(true);
    try {
      // Get overall stats
      const { data: statsData, error: statsError } = await supabase
        .from('crm_hot_lead_queue')
        .select('status, batch_id', { count: 'exact' });

      if (statsError) throw statsError;

      // Calculate stats
      const stats = {
        total: statsData?.length || 0,
        available: (statsData || []).filter(s => s.status === 'available').length,
        claimed: (statsData || []).filter(s => s.status === 'claimed').length,
        converted: (statsData || []).filter(s => s.status === 'converted').length,
        recycled: (statsData || []).filter(s => s.status === 'recycled').length,
      };
      setQueueStats(stats);

      // Get batch breakdown
      const batchCounts = {};
      (statsData || []).forEach(row => {
        if (!batchCounts[row.batch_id]) {
          batchCounts[row.batch_id] = { available: 0, claimed: 0, converted: 0, recycled: 0 };
        }
        batchCounts[row.batch_id][row.status] = (batchCounts[row.batch_id][row.status] || 0) + 1;
      });

      const batchList = Object.entries(batchCounts).map(([name, counts]) => ({
        name,
        ...counts,
        total: Object.values(counts).reduce((a, b) => a + b, 0),
      }));
      setBatches(batchList);

    } catch (error) {
      console.error('Error fetching queue stats:', error);
      toast?.({
        title: 'Error',
        description: 'Failed to fetch queue statistics',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Initial fetch
  useEffect(() => {
    fetchQueueStats();
  }, [fetchQueueStats]);

  /**
   * Handle file selection
   */
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCsvFile(file);
    setParsedRecords(null);

    // Parse CSV
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setParsedRecords(results.data);
        
        // Auto-generate batch name from filename
        if (!batchName) {
          const baseName = file.name.replace(/\.csv$/i, '').replace(/[^a-zA-Z0-9-_]/g, '_');
          const date = new Date().toISOString().split('T')[0];
          setBatchName(`${baseName}_${date}`);
        }
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        toast?.({
          title: 'CSV Parse Error',
          description: error.message,
          status: 'error',
          duration: 5000,
        });
      },
    });
  }, [batchName, toast]);

  /**
   * Handle import
   */
  const handleImport = useCallback(async () => {
    if (!parsedRecords || parsedRecords.length === 0) {
      toast?.({
        title: 'No Data',
        description: 'Please select a CSV file first',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!batchName.trim()) {
      toast?.({
        title: 'Batch Name Required',
        description: 'Please enter a batch name for this import',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setImporting(true);
    setImportProgress(10);

    try {
      const functions = getFunctions(undefined, 'us-east1');
      const ingestHotLeadCSV = httpsCallable(functions, 'ingestHotLeadCSV');

      setImportProgress(30);

      const result = await ingestHotLeadCSV({
        records: parsedRecords,
        batchId: batchName.trim(),
        checkDuplicates: true,
      });

      setImportProgress(100);
      setLastImportResult(result.data);

      // Show result modal
      onResultOpen();

      // Refresh stats
      await fetchQueueStats();

      // Clear form
      setCsvFile(null);
      setParsedRecords(null);
      setBatchName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      toast?.({
        title: 'Import Complete',
        description: `Imported ${result.data.summary?.inserted || 0} leads to queue`,
        status: 'success',
        duration: 5000,
      });

    } catch (error) {
      console.error('Import error:', error);
      toast?.({
        title: 'Import Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  }, [parsedRecords, batchName, toast, fetchQueueStats, onResultOpen]);

  /**
   * Handle manual recycle
   */
  const handleManualRecycle = useCallback(async () => {
    setRecycling(true);
    
    try {
      const functions = getFunctions(undefined, 'us-east1');
      const manualRecycleHotLeads = httpsCallable(functions, 'manualRecycleHotLeads');

      const result = await manualRecycleHotLeads({ recycleAll: true });

      toast?.({
        title: 'Recycle Complete',
        description: `Recycled ${result.data.recycled || 0} stale leads`,
        status: 'success',
        duration: 5000,
      });

      // Refresh stats
      await fetchQueueStats();

    } catch (error) {
      console.error('Recycle error:', error);
      toast?.({
        title: 'Recycle Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setRecycling(false);
    }
  }, [toast, fetchQueueStats]);

  if (loading) {
    return (
      <Center py={8}>
        <VStack>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading queue data...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <HStack justify="space-between" align="center">
        <VStack align="start" spacing={0}>
          <Heading size="md">Hot Lead Queue Management</Heading>
          <Text fontSize="sm" color="gray.600">
            Import and manage portfolio refinance opportunities
          </Text>
        </VStack>
        <HStack>
          <Button
            as={RouterLink}
            to="/crm/next-lead-up"
            leftIcon={<FiExternalLink />}
            colorScheme="blue"
            variant="outline"
            size="sm"
          >
            View Queue
          </Button>
          <Button
            leftIcon={<FiRefreshCw />}
            onClick={fetchQueueStats}
            size="sm"
          >
            Refresh
          </Button>
        </HStack>
      </HStack>

      <Divider />

      {/* Statistics */}
      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
        <Stat bg={cardBg} p={4} borderRadius="lg">
          <StatLabel>Total Leads</StatLabel>
          <StatNumber>{queueStats?.total || 0}</StatNumber>
          <StatHelpText>All time</StatHelpText>
        </Stat>
        <Stat bg={cardBg} p={4} borderRadius="lg">
          <StatLabel>Available</StatLabel>
          <StatNumber color="green.500">{queueStats?.available || 0}</StatNumber>
          <StatHelpText>Ready to claim</StatHelpText>
        </Stat>
        <Stat bg={cardBg} p={4} borderRadius="lg">
          <StatLabel>Claimed</StatLabel>
          <StatNumber color="blue.500">{queueStats?.claimed || 0}</StatNumber>
          <StatHelpText>Being worked</StatHelpText>
        </Stat>
        <Stat bg={cardBg} p={4} borderRadius="lg">
          <StatLabel>Converted</StatLabel>
          <StatNumber color="purple.500">{queueStats?.converted || 0}</StatNumber>
          <StatHelpText>Deals created</StatHelpText>
        </Stat>
        <Stat bg={cardBg} p={4} borderRadius="lg">
          <StatLabel>Recycled</StatLabel>
          <StatNumber color="orange.500">{queueStats?.recycled || 0}</StatNumber>
          <StatHelpText>Returned to pool</StatHelpText>
        </Stat>
      </SimpleGrid>

      <Divider />

      {/* Import Section */}
      <Box>
        <Heading size="sm" mb={4}>Import Hot Leads</Heading>
        
        <VStack align="stretch" spacing={4}>
          <FormControl>
            <FormLabel>CSV File</FormLabel>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              p={1}
            />
            <FormHelperText>
              Upload a refinance opportunities CSV with Deal_Score, Loan_Summary, borrower info, etc.
            </FormHelperText>
          </FormControl>

          {parsedRecords && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <AlertDescription>
                Parsed {parsedRecords.length} records from CSV
              </AlertDescription>
            </Alert>
          )}

          <FormControl>
            <FormLabel>Batch Name</FormLabel>
            <Input
              placeholder="e.g., Feb_Refi_Drop_2026-02-05"
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
            />
            <FormHelperText>
              A unique identifier for this import batch
            </FormHelperText>
          </FormControl>

          {importing && (
            <Progress 
              value={importProgress} 
              size="sm" 
              colorScheme="blue" 
              borderRadius="full" 
            />
          )}

          <HStack>
            <Button
              leftIcon={<FiUpload />}
              colorScheme="blue"
              onClick={handleImport}
              isLoading={importing}
              loadingText="Importing..."
              isDisabled={!parsedRecords || !batchName.trim()}
            >
              Import to Queue
            </Button>
          </HStack>
        </VStack>
      </Box>

      <Divider />

      {/* Batch Management */}
      <Box>
        <HStack justify="space-between" mb={4}>
          <Heading size="sm">Batches</Heading>
          <Button
            leftIcon={<FiRefreshCw />}
            colorScheme="orange"
            variant="outline"
            size="sm"
            onClick={handleManualRecycle}
            isLoading={recycling}
            loadingText="Recycling..."
          >
            Recycle Stale Leads
          </Button>
        </HStack>

        {batches.length === 0 ? (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              No batches found. Import your first CSV to get started.
            </AlertDescription>
          </Alert>
        ) : (
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Batch Name</Th>
                  <Th isNumeric>Total</Th>
                  <Th isNumeric>Available</Th>
                  <Th isNumeric>Claimed</Th>
                  <Th isNumeric>Converted</Th>
                  <Th isNumeric>Recycled</Th>
                </Tr>
              </Thead>
              <Tbody>
                {batches.map((batch) => (
                  <Tr key={batch.name}>
                    <Td fontWeight="medium">{batch.name}</Td>
                    <Td isNumeric>{batch.total}</Td>
                    <Td isNumeric>
                      <Badge colorScheme="green">{batch.available || 0}</Badge>
                    </Td>
                    <Td isNumeric>
                      <Badge colorScheme="blue">{batch.claimed || 0}</Badge>
                    </Td>
                    <Td isNumeric>
                      <Badge colorScheme="purple">{batch.converted || 0}</Badge>
                    </Td>
                    <Td isNumeric>
                      <Badge colorScheme="orange">{batch.recycled || 0}</Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      {/* Import Result Modal */}
      <Modal isOpen={isResultOpen} onClose={onResultClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Icon as={FiCheckCircle} color="green.500" />
              <Text>Import Complete</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {lastImportResult && (
              <VStack align="stretch" spacing={4}>
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    {lastImportResult.message}
                  </AlertDescription>
                </Alert>

                <SimpleGrid columns={2} spacing={4}>
                  <Box bg={cardBg} p={4} borderRadius="md">
                    <Text fontSize="sm" color="gray.600">Total Rows</Text>
                    <Text fontSize="xl" fontWeight="bold">
                      {lastImportResult.summary?.totalRows || 0}
                    </Text>
                  </Box>
                  <Box bg={cardBg} p={4} borderRadius="md">
                    <Text fontSize="sm" color="gray.600">Inserted</Text>
                    <Text fontSize="xl" fontWeight="bold" color="green.500">
                      {lastImportResult.summary?.inserted || 0}
                    </Text>
                  </Box>
                  <Box bg={cardBg} p={4} borderRadius="md">
                    <Text fontSize="sm" color="gray.600">Duplicates Found</Text>
                    <Text fontSize="xl" fontWeight="bold" color="yellow.500">
                      {lastImportResult.summary?.duplicatesFound || 0}
                    </Text>
                  </Box>
                  <Box bg={cardBg} p={4} borderRadius="md">
                    <Text fontSize="sm" color="gray.600">Errors</Text>
                    <Text fontSize="xl" fontWeight="bold" color="red.500">
                      {lastImportResult.summary?.errors || 0}
                    </Text>
                  </Box>
                </SimpleGrid>

                {lastImportResult.errors?.length > 0 && (
                  <Box>
                    <Text fontWeight="medium" mb={2}>Errors:</Text>
                    <Box bg="red.50" p={3} borderRadius="md" maxH="150px" overflowY="auto">
                      {lastImportResult.errors.map((err, i) => (
                        <Text key={i} fontSize="sm" color="red.700">{err}</Text>
                      ))}
                    </Box>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onResultClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

export default HotLeadQueueManager;
